import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const SITE_URL = 'https://www.afroscapescurls.com'
const SITE_NAME = 'The House of Afros, Capes & Curls'
const DEFAULT_IMAGE = `${SITE_URL}/img/HOACC.png`

const AFROCON_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'AfroCon 2026: Cool Girls Love Anime',
    startDate: '2026-10-10T10:00:00-05:00',
    endDate: '2026-10-10T18:00:00-05:00',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: [`${SITE_URL}/img/events/2026/afrocon-2026.png`],
    description: 'A celebration of the women, girls, and communities that love anime.',
    location: {
        '@type': 'Place',
        name: 'MCC Fort Omaha Campus, CAET Building 24',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '5300 N 30th St',
            addressLocality: 'Omaha',
            addressRegion: 'NE',
            postalCode: '68111',
            addressCountry: 'US'
        }
    },
    organizer: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL
    },
    offers: {
        '@type': 'Offer',
        price: '10',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://www.eventbrite.com/e/afrocon-2026-tickets-1979103935146'
    }
}

type SeoRecord = {
    title: string
    description: string
    image?: string
    noIndex?: boolean
}

const SEO_BY_PATH: Record<string, SeoRecord> = {
    '/': {
        title: 'The House of Afros, Capes & Curls | Omaha Geek Culture Community',
        description:
            'Building an inclusive Omaha community through Afrofuturism, science fiction, fantasy, gaming, education, and year-round cultural events.'
    },
    '/welcome': {
        title: `Welcome | ${SITE_NAME}`,
        description: 'Enter The House of Afros, Capes & Curls.',
        noIndex: true
    },
    '/about': {
        title: `About Us | ${SITE_NAME}`,
        description: 'Learn about our mission to build belonging, representation, and connection through geek culture in Omaha.'
    },
    '/meet-the-team': {
        title: `Meet the Team | ${SITE_NAME}`,
        description: 'Meet the staff, board, and advisory council guiding The House of Afros, Capes & Curls.'
    },
    '/community-engagement': {
        title: `Community Engagement in Omaha | ${SITE_NAME}`,
        description: 'Explore programs supporting Black creativity, community connection, equity, and cultural engagement in Omaha.'
    },
    '/education': {
        title: `Education Programs | ${SITE_NAME}`,
        description: 'Discover youth education and Young History Detectives programs connecting history, storytelling, creativity, and play.'
    },
    '/special-events': {
        title: `Omaha Events Calendar | ${SITE_NAME}`,
        description: 'Find upcoming House events, workshops, game nights, cultural programs, and community gatherings in Omaha.'
    },
    '/signature-events': {
        title: `Signature Events | AfroCon, Black Fae Day & More`,
        description: 'Explore AfroCon and other signature events celebrating Black creativity, fandom, history, and community in Omaha.',
        image: `${SITE_URL}/img/events/2026/afrocon-2026.png`
    },
    '/juneteenth': {
        title: `Juneteenth Events in Omaha | ${SITE_NAME}`,
        description: 'Celebrate Juneteenth through history, imagination, performance, family activities, and community in Omaha.'
    },
    '/book-club': {
        title: `The House Book Club | ${SITE_NAME}`,
        description: 'Join an inclusive book community exploring mysteries, speculative fiction, fantasy, horror, and works by Black authors.'
    },
    '/photos': {
        title: `Event Photos | ${SITE_NAME}`,
        description: 'View community highlights from events hosted by The House of Afros, Capes & Curls.',
        noIndex: true
    }
}

function setMeta(selector: string, attributes: Record<string, string>) {
    let element = document.head.querySelector<HTMLMetaElement>(selector)
    if (!element) {
        element = document.createElement('meta')
        document.head.appendChild(element)
    }
    Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value))
}

const Seo: React.FC = () => {
    const { pathname } = useLocation()
    const seo =
        SEO_BY_PATH[pathname] ??
        ({
            title: `Page Not Found | ${SITE_NAME}`,
            description: 'The requested page could not be found.',
            noIndex: true
        } satisfies SeoRecord)

    useEffect(() => {
        const canonicalUrl = `${SITE_URL}${pathname === '/' ? '' : pathname}`
        const image = seo.image ?? DEFAULT_IMAGE

        document.title = seo.title
        setMeta('meta[name="description"]', { name: 'description', content: seo.description })
        setMeta('meta[name="robots"]', { name: 'robots', content: seo.noIndex ? 'noindex,follow' : 'index,follow' })
        setMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title })
        setMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description })
        setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
        setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
        setMeta('meta[property="og:image"]', { property: 'og:image', content: image })
        setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
        setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title })
        setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description })
        setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image })

        let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
        if (!canonical) {
            canonical = document.createElement('link')
            canonical.rel = 'canonical'
            document.head.appendChild(canonical)
        }
        canonical.href = canonicalUrl
    }, [pathname, seo])

    return (
        <>
            {(pathname === '/' || pathname === '/signature-events') && (
                <script type="application/ld+json">{JSON.stringify(AFROCON_SCHEMA)}</script>
            )}
        </>
    )
}

export default Seo
